import { load as loadFingerprintAgent } from "@fingerprintjs/fingerprintjs-pro";
import { type AnalyticsEventPayloads, stAnalytics } from "@storytell/analytics";
import {
  type AuthServiceMethods,
  type AuthStore,
  createTokenRefresher,
  firebaseConfig,
  initialAuthStore,
  type MarketingOptIns,
  prepareSyncRequest,
} from "@storytell/auth";
import { Auth, create, useAuthClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { SyncResponse } from "@storytell/cp-client/src/gen/auth/v3/auth_pb";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { type Logger, Named } from "@storytell/logger";
import { isError, isSuccess, newErrorResult, newSuccessResult, type Result } from "@storytell/result";
import { BrowserStorage } from "@storytell/storage";
import { type FirebaseError, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import {
  EmailAuthProvider,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  type UserCredential,
} from "firebase/auth";
import { type Accessor, type InitializedResourceReturn, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { forgetSession, listenForAuthChanges, rememberSession, type SyncIdentity } from "./authListeners";
import { clearLastProjectCookie, clearSignedInCookie } from "./redirectHintCookies";

declare const RUNNING_ENV: string;
declare const FIREBASE_APP_CHECK_SITE_KEY: string;
declare const FINGERPRINTJS_PUBLIC_KEY: string;

declare global {
  interface Window {
    fpr?: (command: "referral", payload: { email: string }) => void;
  }
}

const trackSignupConversion = (email: string, userId: string): void => {
  try {
    window.fpr?.("referral", { email });
    if ("dataLayer" in window && Array.isArray(window.dataLayer)) {
      window.dataLayer.push("event", "conversion_event_signup", { email, userId });
    }
  } catch {
    /* conversion tracking is best-effort */
  }
};

let fpAgentPromise: ReturnType<typeof loadFingerprintAgent> | null = null;

const getFPAgent = () => {
  if (isServer) return null;
  if (!FINGERPRINTJS_PUBLIC_KEY) return null;
  if (!fpAgentPromise) {
    fpAgentPromise = loadFingerprintAgent({
      apiKey: FINGERPRINTJS_PUBLIC_KEY,
      endpoint: "/fp",
      scriptUrlPattern: "/fp/web/v<version>/<apiKey>/loader_v<loaderVersion>.js",
    }).catch((error) => {
      fpAgentPromise = null;
      throw error;
    });
  }
  return fpAgentPromise;
};

// The auth service's error envelope carries its user-facing sentence in "humane".
const humaneMessage = (body: unknown): string | undefined =>
  typeof body === "object" && body !== null && "humane" in body && typeof body.humane === "string"
    ? body.humane
    : undefined;

const isFirebaseError = (error: unknown): error is FirebaseError =>
  error instanceof Error && "code" in error && typeof error.code === "string";

const FP_TIMEOUT_MS = 10_000;
const FP_TIMEOUT_MESSAGE = "Fingerprint verification timed out";

// Returns the FingerprintJS request id, or undefined when fingerprinting is disabled
// (SSR or no public key). Throws on capture failure after emitting a PostHog event so
// the caller can decide whether to fail closed (magic link) or fail open (Google).
const captureFingerprint = async (
  flow: AnalyticsEventPayloads["auth_fingerprint_failed"]["flow"]
): Promise<string | undefined> => {
  const fpAgent = getFPAgent();
  if (!fpAgent) return undefined;

  const startedAt = Date.now();
  let step: "load" | "get" = "load";
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(FP_TIMEOUT_MESSAGE)), FP_TIMEOUT_MS);
  });

  try {
    const agent = await Promise.race([fpAgent, timeout]);
    step = "get";
    const result = await Promise.race([agent.get(), timeout]);
    return result.requestId;
  } catch (error) {
    const isTimeout = error instanceof Error && error.message === FP_TIMEOUT_MESSAGE;
    const reason = isTimeout ? "timeout" : step === "load" ? "load_failed" : "get_failed";
    try {
      void stAnalytics.track("auth_fingerprint_failed", {
        flow,
        reason,
        durationMs: Date.now() - startedAt,
      });
    } catch (trackErr) {
      console.error("auth_fingerprint_failed track error:", trackErr);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL = "lumle_v2";
export const BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS = "st_oauth_redirect";
const BROWSER_STORAGE_KEY_MARKETING_OPT_INS = "st_marketing_opt_ins";

const getAuthServiceURL = (): string => {
  if (RUNNING_ENV === "local") return "http://localhost:14040";
  if (RUNNING_ENV === "development") return "https://auth.storytell-development.ai";
  return "https://auth.storytell.ai";
};

export const useAuthBrowserMethods = (deps: {
  logger: Logger;
  resource: InitializedResourceReturn<AuthStore>;
  deviceId: Accessor<string>;
  locale: Accessor<string>;
  userAgent: Accessor<string>;
}): AuthServiceMethods => {
  const logger = new Named(deps.logger, "authBrowser");
  const { resource, deviceId, locale, userAgent } = deps;
  const [data, { mutate }] = resource;

  const firebaseInstance = initializeApp(firebaseConfig());
  const auth = getAuth(firebaseInstance);

  const setToken = (token: string) => {
    mutate((p) => ({ ...p, token }));
    rememberSession(localStorage, auth.currentUser, token);
  };

  const refresher = createTokenRefresher(auth, (token: string) => setToken(token), logger);

  if (RUNNING_ENV === "production") {
    initializeAppCheck(firebaseInstance, {
      provider: new ReCaptchaEnterpriseProvider(FIREBASE_APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  }

  onMount(() => {
    if (isServer) return;
    refresher.schedule(5 * 60);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        logger.info("tab became active, refreshing auth token");
        refresher.refresh(false).catch((error: unknown) => {
          logger.error("token_refresh_failed", "error refreshing auth token on tab focus", error);
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const refreshOnReconnect = async () => {
      logger.info("connectivity restored, refreshing auth token");
      try {
        await refresher.refresh(true);
        if (auth.currentUser) {
          const syncResponse = await syncAuthWithAPI(auth.currentUser);
          if (isSuccess(syncResponse)) {
            const authenticatedUser = syncResponse.data.authenticatedUser;
            mutate((p) => ({
              ...p,
              state: authenticatedUser.user.lifecycleStatus,
              authenticatedUser,
            }));
          }
        }
        // Fan out auth-token-refreshed so identity stores can recover without
        // piggy-backing on refresh-sync, which fires on every content mutation.
        dispatchGlobalEvent("refresh-sync", undefined);
        dispatchGlobalEvent("auth-token-refreshed", undefined);
      } catch (error) {
        logger.error("connectivity_restoration_error", "error during connectivity restoration", error);
      }
    };
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent(
      "connectivity-restored",
      () => void refreshOnReconnect()
    );

    onCleanup(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribeConnectivityRestored();
      refresher.clear();
    });
  });

  // The sign-up card's answers wait in session storage until a sync has carried them, so a
  // failed sign-up sync retried on reload still sends them instead of the server's default.
  const readStoredOptIns = (): MarketingOptIns | undefined => {
    const stored = sessionStorage.getItem(BROWSER_STORAGE_KEY_MARKETING_OPT_INS);
    if (!stored) return undefined;
    let parsed: unknown;
    try {
      parsed = JSON.parse(stored);
    } catch {
      return undefined;
    }
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !("news" in parsed && typeof parsed.news === "boolean") ||
      !("changelog" in parsed && typeof parsed.changelog === "boolean")
    ) {
      return undefined;
    }
    return { news: parsed.news, changelog: parsed.changelog };
  };

  const syncAuthWithAPI = async (firebaseUser: SyncIdentity): Promise<Result<SyncResponse>> => {
    logger.info("syncAuthWithAPI", { firebaseUser });

    const request = prepareSyncRequest(firebaseUser, deviceId(), locale(), userAgent());
    const optIns = readStoredOptIns();
    if (optIns) {
      request.marketingOptIns = create(Auth.MarketingOptInsSchema, optIns);
    }

    const fpRequestId = sessionStorage.getItem("st_fp_request_id");
    if (fpRequestId) {
      request.fingerprintRequestId = fpRequestId;
      sessionStorage.removeItem("st_fp_request_id");
    }

    const result = await useAuthClient().sync(request, useCallOptions(withBearerToken(data().token)));

    if (result.isOk()) {
      sessionStorage.removeItem(BROWSER_STORAGE_KEY_MARKETING_OPT_INS);
      if (result.value.tokenRefreshReason) {
        logger.info("token refresh requested by server, refreshing token");
        const newToken = await firebaseUser.getIdToken(true);
        setToken(newToken);
      }
    }

    if (result.isErr()) {
      return newErrorResult(result.error.code, result.error.message);
    }

    return newSuccessResult(result.value);
  };

  const signInWithMagicLink = async (
    email: string,
    target?: { type: "project"; inviteUrl: string } | { type: "redirect"; redirectUrl: string },
    optIns?: MarketingOptIns
  ) => {
    try {
      void stAnalytics.track("log_in_started", { flow: "magic-link" });
    } catch (error) {
      console.error(error);
    }

    // Build redirect URL - if there's a project invite, use it directly as the post-auth destination
    // The backend will return this URL after successful verification
    let redirectUrl: string | undefined;
    if (target?.type === "project") {
      redirectUrl = target.inviteUrl;
    } else if (target?.type === "redirect" && target.redirectUrl.startsWith("/")) {
      redirectUrl = target.redirectUrl;
    }

    let fingerprintRequestId: string | undefined;
    try {
      fingerprintRequestId = await captureFingerprint("magic-link");
    } catch (error) {
      console.error("Fingerprint agent error:", error);
      throw new Error(
        "Security verification failed. Please try disabling any ad blockers or privacy extensions and reload the page.",
        { cause: error }
      );
    }

    // Call our backend endpoint instead of Firebase's sendSignInLinkToEmail
    const response = await fetch(`${getAuthServiceURL()}/v1/magic-link/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        redirect_url: redirectUrl,
        fingerprint_request_id: fingerprintRequestId,
        marketing_opt_ins: optIns,
      }),
    });

    if (!response.ok) {
      const errorBody: unknown = await response.json().catch(() => undefined);
      const error = new Error(humaneMessage(errorBody) || "Failed to send magic link") as Error & {
        status: number;
      };
      error.status = response.status;
      throw error;
    }

    // Store email for later verification (in case user opens link on same device)
    BrowserStorage.set(BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL, email);
  };

  // finishSignInMagicLink finishes the sign in process for a magic link.
  // This function always runs on the client.
  const finishSignInMagicLink = async (email: string, url: string): Promise<Result<null>> => {
    const childLogger = logger.child("finishSignInMagicLink");

    await auth.authStateReady().catch(() => null);

    mutate(initialAuthStore());

    if (!auth.currentUser) {
      return newErrorResult("user_not_found", "User not found", "User not found");
    }

    if (!auth.currentUser.isAnonymous) {
      // TODO: i18n
      return newSuccessResult(null, undefined, "Already signed in", "current user is not anonymous");
    }

    const emailCredential = EmailAuthProvider.credentialWithLink(email, url);
    let credential: UserCredential;

    try {
      credential = await linkWithCredential(auth.currentUser, emailCredential);
    } catch (error) {
      if (isFirebaseError(error) && error.code === "auth/email-already-in-use") {
        credential = await signInWithCredential(auth, emailCredential);
      } else {
        return newErrorResult(
          isFirebaseError(error) ? error.code : undefined,
          error instanceof Error ? error.message : undefined
        );
      }
    }

    BrowserStorage.remove(BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL);

    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      return newErrorResult("token_not_found", "Token not found", "Token not found");
    }
    setToken(token);

    const syncResult = await syncAuthWithAPI(credential.user);
    if (isError(syncResult)) {
      return newErrorResult(syncResult.code, syncResult.message, syncResult.detail);
    }

    const didSignUp = syncResult.data.authenticatedUser.signedUp;
    const authenticatedUser = syncResult.data.authenticatedUser;
    try {
      if (didSignUp) {
        trackSignupConversion(authenticatedUser.user.primaryEmail, authenticatedUser.user.id);
      }
      await stAnalytics.track(didSignUp ? "sign_up_successful" : "log_in_successful", {
        userId: authenticatedUser.user.id,
      });
    } catch (error) {
      // TODO: capture with sentry while not allowing the user to be impacted
      childLogger.warn("Failed to track analytics", { error });
    }

    mutate((p) => ({
      ...p,
      state: authenticatedUser.user.lifecycleStatus,
      authenticatedUser,
    }));

    return newSuccessResult(null);
  };

  const isURLMagicLink = (url: string) => {
    try {
      const urlObj = new URL(url);
      // New format: /auth/verify?token=xxx
      if (urlObj.pathname === "/auth/verify" && urlObj.searchParams.has("token")) {
        return true;
      }
    } catch {
      // Invalid URL, fall through to legacy check
    }
    // Legacy Firebase format (for backward compatibility)
    return isSignInWithEmailLink(auth, url);
  };

  const signInWithGoogle: AuthServiceMethods["signInWithGoogle"] = async (optIns) => {
    // The popup has to open inside the click's user activation, so only wait for auth when it isn't ready.
    let user = auth.currentUser;
    if (!user) {
      await auth.authStateReady().catch(() => null);
      user = auth.currentUser;
    }

    if (!user) {
      return "not_signed_in";
    }

    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    auth.languageCode = deps.locale();

    if (optIns) {
      sessionStorage.setItem(BROWSER_STORAGE_KEY_MARKETING_OPT_INS, JSON.stringify(optIns));
    } else {
      sessionStorage.removeItem(BROWSER_STORAGE_KEY_MARKETING_OPT_INS);
    }

    let abandoned = false;
    // A newer call cancelled this popup; the session storage now belongs to that call.
    let replaced = false;
    const storeFingerprint = async (): Promise<void> => {
      try {
        const requestId = await captureFingerprint("google");
        if (requestId && !abandoned) sessionStorage.setItem("st_fp_request_id", requestId);
      } catch (error) {
        logger.warn("fingerprint capture failed before Google sign-in, proceeding without it", { error });
      }
    };
    const fingerprint = storeFingerprint();

    try {
      let result: UserCredential;
      try {
        result = await linkWithPopup(user, provider);
        isProcessingTokenSignIn = true;
      } catch (error) {
        switch ((error as { code?: string } | null)?.code) {
          // Popups that cannot open fall back to the redirect flow.
          case "auth/popup-blocked":
          case "auth/operation-not-supported-in-this-environment":
            await fingerprint;
            sessionStorage.setItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS, "true");
            return await linkWithRedirect(user, provider);
          case "auth/popup-closed-by-user":
            sessionStorage.removeItem(BROWSER_STORAGE_KEY_MARKETING_OPT_INS);
            return "not_signed_in";
          case "auth/cancelled-popup-request":
            replaced = true;
            return "replaced";
          case "auth/credential-already-in-use":
          case "auth/email-already-in-use": {
            // Signing in with Google's credential replaces the anonymous session when that credential
            // already belongs to another Firebase user. Switching users would otherwise make
            // onAuthStateChanged sync this sign-in a second time.
            // https://firebase.google.com/docs/auth/web/google-signin#handling-account-exists-with-different-credential
            logger.info("OAuth link conflict, signing in with Google credential", { error });
            isProcessingTokenSignIn = true;
            const googleCredential = GoogleAuthProvider.credentialFromError(error as FirebaseError);
            if (!googleCredential) throw error;
            result = await signInWithCredential(auth, googleCredential);
            break;
          }
          default:
            logger.error("google_sign_in_failed", "Google sign-in failed", error);
            throw error;
        }
      }

      await fingerprint;
      const sync = await completeGoogleSignIn(result);
      if (!sync || isError(sync)) {
        const error = new Error("Google sign-in could not be completed");
        logger.error("google_sign_in_failed", "Google sign-in failed", error);
        throw error;
      }
      return "signed_in";
    } finally {
      // A fingerprint still in flight belongs to this attempt only; the next sync must not pick it up.
      abandoned = true;
      if (!replaced) {
        isProcessingTokenSignIn = false;
        sessionStorage.removeItem("st_fp_request_id");
      }
    }
  };

  const completeGoogleSignIn = async (result: UserCredential) => {
    const childLogger = logger.child("completeGoogleSignIn");

    const credential = GoogleAuthProvider.credentialFromResult(result);

    if (!credential) {
      return null;
    }

    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    setToken(token);

    const syncResponse = await syncAuthWithAPI(result.user);
    childLogger.info("response from syncAuthWithAPI", { syncResponse });

    if (isSuccess(syncResponse)) {
      const authenticatedUser = syncResponse.data.authenticatedUser;

      try {
        if (authenticatedUser.signedUp) {
          trackSignupConversion(authenticatedUser.user.primaryEmail, authenticatedUser.user.id);
        }

        await stAnalytics.track(authenticatedUser.signedUp ? "sign_up_successful" : "log_in_successful", {
          userId: authenticatedUser.user.id,
        });
      } catch {
        /* no-op */
      }
      mutate((p) => ({
        ...p,
        state: authenticatedUser.user.lifecycleStatus,
        authenticatedUser,
      }));
    }

    return syncResponse;
  };

  const processOAuthRedirect = async () => {
    const childLogger = logger.child("processOAuthRedirect");

    const clearOAuthRedirectFlag = () => {
      sessionStorage.removeItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS);
    };

    let result: UserCredential | null = null;
    await auth.authStateReady().catch(() => null);

    try {
      result = await getRedirectResult(auth);
    } catch (error) {
      // Linking Google to the anonymous session via linkWithRedirect fails when:
      // - auth/credential-already-in-use: Google is already linked to another Firebase user
      // - auth/email-already-in-use: same email exists on another account (e.g. magic link)
      // Recover by signing in with the Google credential (same pattern as finishSignInMagicLink).
      // https://firebase.google.com/docs/auth/web/google-signin#handling-account-exists-with-different-credential
      const oauthLinkConflictCodes = ["auth/credential-already-in-use", "auth/email-already-in-use"];
      if (isFirebaseError(error) && oauthLinkConflictCodes.includes(error.code)) {
        childLogger.info("OAuth link conflict, signing in with Google credential", {
          code: error.code,
          error,
        });
        const googleCredential = GoogleAuthProvider.credentialFromError(error);
        if (!googleCredential) {
          childLogger.warn("OAuth link conflict but missing oauth tokens on error", { error });
          clearOAuthRedirectFlag();
          throw error;
        }
        result = await signInWithCredential(auth, googleCredential);
      } else {
        clearOAuthRedirectFlag();
        throw error;
      }
    }
    if (!result) {
      clearOAuthRedirectFlag();
      return;
    }

    logger.info("OAuthRedirect obtained result", { result });

    const sync = await completeGoogleSignIn(result);
    clearOAuthRedirectFlag();
    return sync ? true : undefined;
  };

  const onUnauthenticated = async () => {
    logger.info("signOut");
    mutate(initialAuthStore());
    await signInAnonymously(auth);
  };

  // Flag to prevent duplicate syncAuthWithAPI calls when completeSignInWithToken is in progress.
  // This handles the race condition where signInWithCustomToken triggers onAuthStateChanged,
  // which would otherwise call syncAuthWithAPI while completeSignInWithToken is also calling it.
  let isProcessingTokenSignIn = false;

  const listen = async () => {
    if (isServer) return;

    await listenForAuthChanges({
      redirectInProgress: sessionStorage.getItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS) === "true",
      storage: localStorage,
      // Only reached when the early sync asks for a refreshed token, which needs the SDK's user.
      refreshIdToken: async (uid, forceRefresh) => {
        await auth.authStateReady();
        if (auth.currentUser?.uid !== uid) {
          throw new Error("remembered user was replaced before its token refresh");
        }
        return auth.currentUser.getIdToken(forceRefresh);
      },
      processOAuthRedirect,
      clearRedirectFlag: () => sessionStorage.removeItem(BROWSER_STORAGE_KEY_OAUTH_REDIRECT_IN_PROGRESS),
      logger,
      onAuthStateChanged: (listener) =>
        onAuthStateChanged(auth, (user) => {
          listener(user).catch((error: unknown) => {
            logger.error("auth_state_change_failed", "handling an auth state change failed", error);
          });
        }),
      currentIdToken: async () => auth.currentUser?.getIdToken(),
      setToken,
      isProcessingTokenSignIn: () => isProcessingTokenSignIn,
      sync: async (user) => {
        const syncResponse = await syncAuthWithAPI(user);
        return isSuccess(syncResponse) ? syncResponse.data.authenticatedUser : undefined;
      },
      setAuthenticatedUser: (authenticatedUser) =>
        mutate((p) => ({
          ...p,
          state: authenticatedUser.user.lifecycleStatus,
          authenticatedUser,
        })),
      onUnauthenticated,
    });
  };

  const setupListeners = (): void => {
    listen().catch((error: unknown) => {
      logger.error("auth_listeners_failed", "setting up the auth listeners failed", error);
    });
  };

  const signOut = () => {
    return firebaseSignOut(auth).then((r) => {
      try {
        stAnalytics.plugins.posthog.reset();
      } catch {
        // TODO: capture with sentry but don't allow errors to impact user
      }

      clearSignedInCookie();
      // Sign-out leaves the app for the marketing page, so nothing else gets a
      // chance to drop the redirect target before the next account arrives.
      clearLastProjectCookie();
      // The navigation can beat the signed-out listener, which would leave the session for the next load.
      forgetSession(localStorage);
      window.location.href = "/";
      return r;
    });
  };

  /**
   * Completes sign-in using a Firebase custom token received from the backend callback.
   * This is called by the /auth/verify page after the backend redirects with tokens in URL params.
   *
   * @param firebaseToken - The Firebase custom token from the backend
   * @param isNewUser - Whether this is a new user signup
   * @returns Result with null on success, or error details on failure
   */
  const completeSignInWithToken = async (firebaseToken: string, isNewUser: boolean): Promise<Result<null>> => {
    const childLogger = logger.child("completeSignInWithToken");

    isProcessingTokenSignIn = true;

    try {
      mutate(initialAuthStore());

      const userCredential = await signInWithCustomToken(auth, firebaseToken);

      const idToken = await userCredential.user.getIdToken();
      setToken(idToken);

      BrowserStorage.remove(BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL);

      const syncResult = await syncAuthWithAPI(userCredential.user);
      if (isError(syncResult)) {
        return newErrorResult(syncResult.code, syncResult.message, syncResult.detail);
      }

      const authenticatedUser = syncResult.data.authenticatedUser;

      try {
        if (isNewUser) {
          trackSignupConversion(authenticatedUser.user.primaryEmail, authenticatedUser.user.id);
        }
        await stAnalytics.track(isNewUser ? "sign_up_successful" : "log_in_successful", {
          userId: authenticatedUser.user.id,
        });
      } catch (error) {
        childLogger.warn("Failed to track analytics", { error });
      }

      mutate((p) => ({
        ...p,
        state: authenticatedUser.user.lifecycleStatus,
        authenticatedUser,
      }));

      return newSuccessResult(null);
    } finally {
      isProcessingTokenSignIn = false;
    }
  };

  /**
   * Verifies a magic link token by calling the backend POST /v1/magic-link/verify endpoint.
   * This is part of the 2-step verification flow to prevent spam detectors from consuming tokens.
   *
   * @param magicToken - The magic link token from the URL
   * @returns Result with redirect URL on success, or error details on failure
   */
  const verifyMagicLink = async (magicToken: string): Promise<Result<{ redirectUrl?: string }>> => {
    const childLogger = logger.child("verifyMagicLink");

    isProcessingTokenSignIn = true;

    try {
      const response = await fetch(`${getAuthServiceURL()}/v1/magic-link/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: magicToken }),
      });

      if (!response.ok) {
        const errorBody: unknown = await response.json().catch(() => undefined);
        return newErrorResult("verification_failed", humaneMessage(errorBody) || "Verification failed");
      }

      const responseData: unknown = await response.json();
      const body = typeof responseData === "object" && responseData !== null ? responseData : {};
      const firebaseToken =
        "firebase_token" in body && typeof body.firebase_token === "string" ? body.firebase_token : undefined;
      const isNewUser = "is_new_user" in body && body.is_new_user === true;
      const redirectUrl =
        "redirect_url" in body && typeof body.redirect_url === "string" ? body.redirect_url : undefined;
      const marketingOptIns = "marketing_opt_ins" in body ? body.marketing_opt_ins : undefined;

      if (!firebaseToken) {
        return newErrorResult("no_token", "No Firebase token received");
      }

      mutate(initialAuthStore());

      const userCredential = await signInWithCustomToken(auth, firebaseToken);

      const idToken = await userCredential.user.getIdToken();
      setToken(idToken);

      BrowserStorage.remove(BROWSER_STORAGE_KEY_LAST_USED_MAGIC_LINK_EMAIL);

      if (marketingOptIns) {
        sessionStorage.setItem(BROWSER_STORAGE_KEY_MARKETING_OPT_INS, JSON.stringify(marketingOptIns));
      }

      const syncResult = await syncAuthWithAPI(userCredential.user);
      if (isError(syncResult)) {
        return newErrorResult(syncResult.code, syncResult.message, syncResult.detail);
      }

      const authenticatedUser = syncResult.data.authenticatedUser;

      try {
        if (isNewUser) {
          trackSignupConversion(authenticatedUser.user.primaryEmail, authenticatedUser.user.id);
        }
        await stAnalytics.track(isNewUser ? "sign_up_successful" : "log_in_successful", {
          userId: authenticatedUser.user.id,
        });
      } catch (error) {
        childLogger.warn("Failed to track analytics", { error });
      }

      mutate((p) => ({
        ...p,
        state: authenticatedUser.user.lifecycleStatus,
        authenticatedUser,
      }));

      return newSuccessResult({ redirectUrl });
    } catch (error) {
      childLogger.error("magiclink.verification_failed", "Magic link verification failed", error);
      return newErrorResult("verification_error", error instanceof Error ? error.message : "Verification failed");
    } finally {
      isProcessingTokenSignIn = false;
    }
  };

  return {
    signInWithMagicLink,
    signOut,
    setupListeners,
    signInWithGoogle,
    finishSignInMagicLink,
    isURLMagicLink,
    completeSignInWithToken,
    verifyMagicLink,
  };
};
